import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { getFirestore, doc, getDoc } from 'firebase/firestore';
import ReactMarkdown from 'react-markdown';
import SOPHIELogo from '../assets/SOPHIE_500x.png';
import '../styles/DemoCRM.css';

function DemoCRM() {
    // --- State Definitions ---
    // User and authentication state
    const [user, setUser] = useState(null);
    const [userData, setUserData] = useState(null);
    const [loading, setLoading] = useState(true);

    // Data state for CRM entities
    const [companies, setCompanies] = useState([]);
    const [companiesLoading, setCompaniesLoading] = useState(true);
    const [contacts, setContacts] = useState([]);
    const [contactsLoading, setContactsLoading] = useState(true);
    const [deals, setDeals] = useState([]); // Deals state
    const [dealsLoading, setDealsLoading] = useState(true);

    // Modal state for viewing/editing entities
    const [isCompanyModalOpen, setCompanyModalOpen] = useState(false);
    const [selectedCompany, setSelectedCompany] = useState(null);
    const [isContactModalOpen, setContactModalOpen] = useState(false);
    const [selectedContact, setSelectedContact] = useState(null);
    const [isDealModalOpen, setDealModalOpen] = useState(false);
    const [selectedDeal, setSelectedDeal] = useState(null);

    // Pagination state for companies and contacts
    const [companyPage, setCompanyPage] = useState(1);
    const companiesPerPage = 10;
    const [contactPage, setContactPage] = useState(1);
    const contactsPerPage = 10;

    // State for delete operations and errors
    const [deletingContact, setDeletingContact] = useState(false);
    const [deletingCompany, setDeletingCompany] = useState(false);
    const [deletingDeal, setDeletingDeal] = useState(false);
    const [deleteError, setDeleteError] = useState(null);

    const navigate = useNavigate();

    // --- Modal Handlers ---
    // Open/close company modal
    const openCompanyModal = (company) => {
        setSelectedCompany(company);
        setCompanyModalOpen(true);
    };
    const closeCompanyModal = () => {
        setCompanyModalOpen(false);
        setSelectedCompany(null);
    };

    // Open/close contact modal
    const openContactModal = (contact) => {
        setSelectedContact(contact);
        setContactModalOpen(true);
    };
    const closeContactModal = () => {
        setContactModalOpen(false);
        setSelectedContact(null);
    };

    // Open/close deal modal
    const openDealModal = (deal) => {
        setSelectedDeal(deal);
        setDealModalOpen(true);
    };
    const closeDealModal = () => {
        setDealModalOpen(false);
        setSelectedDeal(null);
    };

    // --- Delete Handlers ---
    // Delete a contact
    const handleDeleteContact = async () => {
        if (!selectedContact || !userData?.company) return;

        try {
            setDeletingContact(true);
            setDeleteError(null);

            await fetchWithRetry(
                `https://sophie-server-1096082289128.europe-west1.run.app/api/contact/${selectedContact.id}?company=${encodeURIComponent(userData.company)}`,
                {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'Origin': window.location.origin
                    }
                }
            );

            setContacts(contacts.filter(contact => contact.id !== selectedContact.id));
            closeContactModal();

        } catch (error) {
            console.error('[SOPHIE-CLIENT] Error deleting contact:', error);
            setDeleteError(error.message);
        } finally {
            setDeletingContact(false);
        }
    };

    // Delete a company
    const handleDeleteCompany = async () => {
        if (!selectedCompany || !userData?.company) return;

        try {
            setDeletingCompany(true);
            setDeleteError(null);

            await fetchWithRetry(
                `https://sophie-server-1096082289128.europe-west1.run.app/api/company/${selectedCompany.id}?company=${encodeURIComponent(userData.company)}`,
                {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'Origin': window.location.origin
                    }
                }
            );

            setCompanies(companies.filter(company => company.id !== selectedCompany.id));
            closeCompanyModal();

        } catch (error) {
            console.error('[SOPHIE-CLIENT] Error deleting company:', error);
            setDeleteError(error.message);
        } finally {
            setDeletingCompany(false);
        }
    };

    // Delete a deal
    const handleDeleteDeal = async () => {
        if (!selectedDeal || !userData?.company) return;

        try {
            setDeletingDeal(true);
            setDeleteError(null);

            await fetchWithRetry(
                `https://sophie-server-1096082289128.europe-west1.run.app/api/deal/${selectedDeal.id}?company=${encodeURIComponent(userData.company)}`,
                {
                    method: 'DELETE',
                    headers: {
                        'Content-Type': 'application/json',
                        'Accept': 'application/json',
                        'Origin': window.location.origin
                    }
                }
            );

            setDeals(deals.filter(deal => deal.id !== selectedDeal.id));
            closeDealModal();

        } catch (error) {
            console.error('[SOPHIE-CLIENT] Error deleting deal:', error);
            setDeleteError(error.message);
        } finally {
            setDeletingDeal(false);
        }
    };

    // --- Authentication and Data Fetching ---
    useEffect(() => {
        const auth = getAuth();

        // Listen for authentication state changes
        const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
            if (currentUser) {
                // User is signed in
                setUser(currentUser);

                // Fetch user data from Firestore
                try {
                    const db = getFirestore();
                    const userDocRef = doc(db, "user_data", currentUser.email);
                    const userDocSnap = await getDoc(userDocRef);

                    if (userDocSnap.exists()) {
                        const userData = userDocSnap.data();

                        // Security check for sophiedoc.ai domain
                        if (
                            userData.company?.toLowerCase() === "sophie" &&
                            !currentUser.email?.toLowerCase().endsWith("@sophiedoc.ai")
                        ) {
                            await auth.signOut();
                            navigate('/');
                            return;
                        }

                        setUserData(userData);
                    }
                } catch (error) {
                    console.error('[SOPHIE-CLIENT] Error fetching user data:', error);
                }
            } else {
                // User is signed out
                navigate('/');
            }
            setLoading(false);
        });

        // Cleanup subscription on unmount
        return () => unsubscribe();
    }, [navigate]);

    // Fetch companies for the user's company
    useEffect(() => {
        if (!userData?.company) return;
        setCompaniesLoading(true);
        
        fetchWithRetry(`https://sophie-server-1096082289128.europe-west1.run.app/api/companies?company=${encodeURIComponent(userData.company)}`)
            .then(data => {
                setCompanies(data);
                setCompaniesLoading(false);
            })
            .catch(err => {
                console.error('[SOPHIE-CLIENT] Failed to fetch companies after retries:', err);
                setCompaniesLoading(false);
            });
    }, [userData?.company]);

    // Fetch contacts for the user's company
    useEffect(() => {
        if (!userData?.company) return;
        setContactsLoading(true);
        
        fetchWithRetry(`https://sophie-server-1096082289128.europe-west1.run.app/api/company-contacts?company=${encodeURIComponent(userData.company)}`)
            .then(data => {
                setContacts(data);
                setContactsLoading(false);
            })
            .catch(err => {
                console.error('[SOPHIE-CLIENT] Failed to fetch contacts after retries:', err);
                setContactsLoading(false);
            });
    }, [userData?.company]);

    // Fetch deals for the user's company
    useEffect(() => {
        if (!userData?.company) return;
        setDealsLoading(true);
        
        fetchWithRetry(`https://sophie-server-1096082289128.europe-west1.run.app/api/deals?company=${encodeURIComponent(userData.company)}`)
            .then(data => {
                setDeals(data);
                setDealsLoading(false);
            })
            .catch(err => {
                console.error('[SOPHIE-CLIENT] Failed to fetch deals after retries:', err);
                setDealsLoading(false);
            });
    }, [userData?.company]);

    // --- Logout Handler ---
    const handleLogout = async () => {
        const auth = getAuth();
        try {
            await auth.signOut();
            navigate('/');
        } catch (error) {
            console.error('[SOPHIE-CLIENT] Logout error:', error);
        }
    };

    // --- Early Loading State ---
    if (loading || !user) {
        return (
            <div className="zero-loading-container">
                <div className="zero-loading-spinner"></div>
                <p>Loading your workspace...</p>
            </div>
        );
    }

    // --- Pagination Calculations ---
    const totalCompanies = companies.length;
    const totalCompanyPages = Math.ceil(totalCompanies / companiesPerPage);
    const companyStartIdx = (companyPage - 1) * companiesPerPage;
    const companyEndIdx = Math.min(companyStartIdx + companiesPerPage, totalCompanies);
    const paginatedCompanies = companies.slice(companyStartIdx, companyEndIdx);

    const totalContacts = contacts.length;
    const totalContactPages = Math.ceil(totalContacts / contactsPerPage);
    const contactStartIdx = (contactPage - 1) * contactsPerPage;
    const contactEndIdx = Math.min(contactStartIdx + contactsPerPage, totalContacts);
    const paginatedContacts = contacts.slice(contactStartIdx, contactEndIdx);

    // --- Kanban Board Preparation ---
    const dealStages = ['Prospecting', 'Appointment Scheduled', 'Contract Sent', 'Closed Won', 'Closed Lost', 'Other'];
    const dealsByStage = dealStages.reduce((acc, stage) => {
        acc[stage] = { deals: [], totalAmount: 0 };
        return acc;
    }, {});

    deals.forEach(deal => {
        const stage = deal.deal_stage && dealStages.includes(deal.deal_stage) ? deal.deal_stage : 'Other';
        if (!dealsByStage[stage]) {
            dealsByStage[stage] = { deals: [], totalAmount: 0 };
        }
        dealsByStage[stage].deals.push(deal);
        dealsByStage[stage].totalAmount += parseFloat(deal.amount) || 0;
    });

    // --- Helpers ---
    // Format currency for deal amounts
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        try {
            return new Date(dateString).toLocaleDateString();
        } catch (e) {
            return dateString;
        }
    };

    // Render icon for each deal stage
    const renderStageIcon = (stage) => {
        switch (stage) {
            case 'Prospecting':
                return (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#60a5fa" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="12" y1="8" x2="12" y2="16"></line>
                        <line x1="8" y1="12" x2="16" y2="12"></line>
                    </svg>
                );
            case 'Appointment Scheduled':
                return (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                        <line x1="16" y1="2" x2="16" y2="6"></line>
                        <line x1="8" y1="2" x2="8" y2="6"></line>
                        <line x1="3" y1="10" x2="21" y2="10"></line>
                    </svg>
                );
            case 'Contract Sent':
                return (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9172f4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                        <polyline points="14 2 14 8 20 8"></polyline>
                        <line x1="16" y1="13" x2="8" y2="13"></line>
                        <line x1="16" y1="17" x2="8" y2="17"></line>
                        <polyline points="10 9 9 9 8 9"></polyline>
                    </svg>
                );
            case 'Closed Won':
                return (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                    </svg>
                );
            case 'Closed Lost':
                return (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <line x1="15" y1="9" x2="9" y2="15"></line>
                        <line x1="9" y1="9" x2="15" y2="15"></line>
                    </svg>
                );
            default:
                return (
                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="1"></circle>
                        <circle cx="19" cy="12" r="1"></circle>
                        <circle cx="5" cy="12" r="1"></circle>
                    </svg>
                );
        }
    };

    // --- Utility function for fetching with retry ---
    const fetchWithRetry = async (url, options = {}, maxRetries = 3, delay = 1000) => {
      let lastError;
      
      for (let attempt = 0; attempt < maxRetries; attempt++) {
        try {
          const response = await fetch(url, options);
          
          if (response.ok) {
            return await response.json();
          }
          
          // If response is not ok, try to parse error message
          const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
          lastError = new Error(`Request failed with status ${response.status}: ${errorData.message || 'No message'}`);
          
          console.log(`[SOPHIE-CLIENT] Attempt ${attempt + 1}/${maxRetries} failed: ${lastError.message}`);
          
        } catch (error) {
          lastError = error;
          console.log(`[SOPHIE-CLIENT] Attempt ${attempt + 1}/${maxRetries} failed: ${error.message}`);
        }
        
        // Don't wait on the last attempt
        if (attempt < maxRetries - 1) {
          // Exponential backoff: delay, delay*2, delay*4, etc.
          const backoffDelay = delay * Math.pow(2, attempt);
          console.log(`[SOPHIE-CLIENT] Retrying in ${backoffDelay}ms...`);
          await new Promise(resolve => setTimeout(resolve, backoffDelay));
        }
      }
      
      throw lastError;
    };

    // --- Main Render ---
    return (
        <div className="zero-crm-container mobile-friendly">
            {/* Main content area including header, dashboard, and sections */}
            <div className="zero-main-content full-width">
                {/* Header with user info and logout */}
                <div className="zero-header">
                    <div className="zero-header-title" style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
                        <img
                            src={SOPHIELogo}
                            alt="SOPHIE Logo"
                            style={{ height: '40px', width: '40px', objectFit: 'contain', borderRadius: '8px', background: '#fff' }}
                        />
                        <h1 style={{ margin: 0 }}>
                            {userData?.company && (
                                <span className="company-badge">
                                    {userData?.company.toUpperCase()}
                                </span>
                            )}
                        </h1>
                    </div>
                    <div className="zero-actions">
                      <div className="zero-user-info-mobile">
                        {/* Ensure user object exists before accessing email */}
                        <span className="zero-user-name">{user?.email}</span>
                      </div>
                      <button onClick={handleLogout} className="zero-action-button zero-logout-action">
                        <span>Logout</span>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                            <polyline points="16 17 21 12 16 7"></polyline>
                            <line x1="21" y1="12" x2="9" y2="12"></line>
                        </svg>
                      </button>
                    </div>
                </div>
                
                <div className="zero-dashboard">
                    <div className="zero-welcome-card">
                        <h2>Your company CRM boosted with AI super powers</h2>
                        <p>Sophie automatically updates your CRM with emails, meeting summaries and follow-ups – and your deal pipeline.</p>
                        <p>Fully compatible with Salesforce and HubSpot.</p>
                    </div>
                    <div className="zero-cards-grid">
                        <div className="zero-card">
                            <div className="zero-card-icon contacts-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                    <circle cx="12" cy="7" r="4"></circle>
                                </svg>
                            </div>
                            <h3>Contacts</h3>
                            <p>Manage your contacts with AI-powered insights</p>
                            <a href="#contacts" className="zero-card-link">Explore Contacts</a>
                        </div>
                        
                        <div className="zero-card">
                            <div className="zero-card-icon companies-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                                </svg>
                            </div>
                            <h3>Companies</h3>
                            <p>Organize and track your business relationships</p>
                            <a href="#companies" className="zero-card-link">View Companies</a>
                        </div>

                        <div className="zero-card">
                            <div className="zero-card-icon deals-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                                    <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                                </svg>
                            </div>
                            <h3>Deals</h3>
                            <p>Track opportunities and manage your pipeline</p>
                            <a href="#deals" className="zero-card-link">View Deals</a>
                        </div>

                        <div className="zero-card">
                            <div className="zero-card-icon assistant-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <path d="m9 12 2 2 4-4"></path>
                                </svg>
                            </div>
                            <h3>AI Meeting Notes</h3>
                            <p>Your SOPHIE AI powered meeting notes assistant that will update Deals for you</p>
                            <a href="#assistant" className="zero-card-link">Get Chrome Extension</a>
                        </div>

                        <div className="zero-card">
                            <div className="zero-card-icon companies-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                    <polyline points="17 8 12 3 7 8"></polyline>
                                    <line x1="12" y1="3" x2="12" y2="15"></line>
                                </svg>
                            </div>
                            <h3>Export to CRM</h3>
                            <p>Export to your existing CRM automatically using SOPHIE AI. Contact us to find out more</p>
                            <div className="crm-logos">
                                <img src="/images/salesforce.png" alt="Salesforce" className="crm-logo" />
                                <img src="/images/hubspot.png" alt="HubSpot" className="crm-logo" />
                            </div>
                            
                        </div>
                        <div className="zero-card">
                            <div className="zero-card-icon assistant-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                    <polyline points="22,6 12,13 2,6"></polyline>
                                </svg>
                            </div>
                            <h3>Email Integration</h3>
                            <p>Start filling your CRM using AI by sending an email to <b>{userData?.company?.toLowerCase()}@sophiedoc.ai</b> address</p>
                            
                        </div>
                        <div className="zero-card">
                            <div className="zero-card-icon assistant-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline>
                                </svg>
                            </div>
                            <h3>Validate your Invoices</h3>
                            <p>
                                Try out SOPHIE invoice matching tool that can be integrated with your ERP.
                            </p>
                            <a
                                href="https://invoice-processing-1096082289128.europe-west1.run.app/"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="zero-card-link"
                            >
                                Try Invoice Tool
                            </a>
                        </div>
                    </div>
                </div>
                


                
                {/* Added placeholder sections for mobile navigation targets */}
                <div id="contacts" className="zero-section">
                    <div className="zero-section-header">
                        <h2>Contacts</h2>
                        <p className="zero-section-description">
                            Manage your contacts and view AI-generated insights.
                        </p>
                    </div>
                    <div className="zero-table-container">
                        <div className="zero-table-header">
                            <div className="zero-table-search">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8"></circle>
                                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                </svg>
                                <input type="text" placeholder="Search contacts..." />
                            </div>
                            <div className="zero-table-actions">
                                <button className="zero-table-button zero-filter-button">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                                    </svg>
                                    Filter
                                </button>
                                <button className="zero-table-button zero-add-button">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="12" y1="5" x2="12" y2="19"></line>
                                        <line x1="5" y1="12" x2="19" y2="12"></line>
                                    </svg>
                                    Add Contact
                                </button>
                            </div>
                        </div>
                        <div className="zero-table">
                            <table>
                                <thead>
                                    <tr>
                                        <th>
                                            <div className="th-content">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4e54f4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                                                    <circle cx="12" cy="7" r="4"></circle>
                                                </svg>
                                                <span>Name</span>
                                            </div>
                                        </th>
                                        <th>
                                            <div className="th-content">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6abeee" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                                                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                                                </svg>
                                                <span>Company</span>
                                            </div>
                                        </th>
                                        <th>
                                            <div className="th-content">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9172f4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <circle cx="12" cy="12" r="10"></circle>
                                                    <line x1="2" y1="12" x2="22" y2="12"></line>
                                                    <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
                                                </svg>
                                                <span>Job Title</span>
                                            </div>
                                        </th>
                                        <th>
                                            <div className="th-content">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ff6a54" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                                                    <polyline points="22,6 12,13 2,6"></polyline>
                                                </svg>
                                                <span>Email</span>
                                            </div>
                                        </th>
                                        <th>
                                            <div className="th-content">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                                                </svg>
                                                <span>Phone</span>
                                            </div>
                                        </th>
                                        <th>
                                            <div className="th-content">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2"></rect>
                                                    <line x1="16" y1="2" x2="16" y2="6"></line>
                                                    <line x1="8" y1="2" x2="8" y2="6"></line>
                                                    <line x1="3" y1="10" x2="21" y2="10"></line>
                                                </svg>
                                                <span>Last Updated</span>
                                            </div>
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {contactsLoading ? (
                                        Array.from({ length: 5 }).map((_, idx) => (
                                            <tr key={idx} className="zero-skeleton-row">
                                                {Array.from({ length: 6 }).map((_, colIdx) => (
                                                    <td key={colIdx}>
                                                        <div className="zero-skeleton-cell"></div>
                                                    </td>
                                                ))}
                                            </tr>
                                        ))
                                    ) : paginatedContacts.length === 0 ? (
                                        <tr>
                                            <td colSpan={6}>No contacts found.</td>
                                        </tr>
                                    ) : (
                                        paginatedContacts.map(contact => (
                                            <tr key={contact.id}>
                                                <td 
                                                    onClick={() => openContactModal(contact)} 
                                                    className="clickable-company-name"
                                                >
                                                    {contact.first_name} {contact.last_name}
                                                </td>
                                                <td>{contact.company_name}</td>
                                                <td>{contact.job_title || '-'}</td>
                                                <td>{contact.email}</td>
                                                <td>{contact.phone_number || contact.mobile_phone_number || '-'}</td>
                                                <td>{contact.updated_at ? new Date(contact.updated_at).toLocaleDateString() : '-'}</td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {/* Contacts Pagination Controls */}
                        <div className="zero-pagination-controls">
                            <div className="zero-pagination-info">
                                Page {contactPage} of {totalContactPages} ({totalContacts} contacts)
                            </div>
                            <div className="zero-pagination-buttons">
                                <button
                                    onClick={() => setContactPage(prev => Math.max(prev - 1, 1))}
                                    disabled={contactPage === 1 || totalContacts === 0}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="15 18 9 12 15 6"></polyline>
                                    </svg>
                                </button>
                                <button
                                    onClick={() => setContactPage(prev => Math.min(prev + 1, totalContactPages))}
                                    disabled={contactPage === totalContactPages || totalContacts === 0}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="9 18 15 12 9 6"></polyline>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div id="companies" className="zero-section">
                    <div className="zero-section-header">
                        <h2>Companies</h2>
                        <p className="zero-section-description">
                            Track and organize company relationships.
                        </p>
                    </div>
                    
                    <div className="zero-table-container">
                        <div className="zero-table-header">
                            <div className="zero-table-search">
                                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="11" cy="11" r="8"></circle>
                                    <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                                </svg>
                                <input type="text" placeholder="Search companies..." />
                            </div>
                            <div className="zero-table-actions">
                                <button className="zero-table-button zero-filter-button">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                                    </svg>
                                    Filter
                                </button>
                                <button className="zero-table-button zero-add-button">
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <line x1="12" y1="5" x2="12" y2="19"></line>
                                        <line x1="5" y1="12" x2="19" y2="12"></line>
                                    </svg>
                                    Add Company
                                </button>
                            </div>
                        </div>
                        <div className="zero-table">
                            <table>
                                <thead>
                                    <tr>
                                        <th>
                                            <div className="th-content">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6abeee" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                                                    <polyline points="9 22 9 12 15 12 15 22"></polyline>
                                                </svg>
                                                <span>Company Name</span>
                                            </div>
                                        </th>
                                        <th>
                                            <div className="th-content">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#ff6a54" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <line x1="6" y1="20" x2="6" y2="10"></line>
                                                    <line x1="12" y1="20" x2="12" y2="4"></line>
                                                    <line x1="18" y1="20" x2="18" y2="16"></line>
                                                </svg>
                                                <span>Industry</span>
                                            </div>
                                        </th>
                                        <th>
                                            <div className="th-content">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#4e54f4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                                                    <circle cx="9" cy="7" r="4"></circle>
                                                    <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                                                    <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
                                                </svg>
                                                <span>Employees</span>
                                            </div>
                                        </th>
                                        <th>
                                            <div className="th-content">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#9172f4" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0 9 9 0 0 1-9 9"></path>
                                                    <circle cx="12" cy="10" r="3"></circle>
                                                </svg>
                                                <span>Location</span>
                                            </div>
                                        </th>
                                        <th>
                                            <div className="th-content">
                                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <rect x="3" y="4" width="18" height="16" rx="2"/>
                                                    <line x1="16" y1="2" x2="16" y2="6"/>
                                                    <line x1="8" y1="2" x2="8" y2="6"/>
                                                    <line x1="3" y1="10" x2="21" y2="10"/>
                                                </svg>
                                                <span>Last Activity</span>
                                            </div>
                                        </th>
                                    
                                        <th></th>
                                    </tr>
                                </thead>
                                <tbody>
                                  {companiesLoading ? (
                                    Array.from({ length: 5 }).map((_, idx) => (
                                      <tr key={idx} className="zero-skeleton-row">
                                        {Array.from({ length: 7 }).map((_, colIdx) => (
                                          <td key={colIdx}>
                                            <div className="zero-skeleton-cell"></div>
                                          </td>
                                        ))}
                                      </tr>
                                    ))
                                  ) : paginatedCompanies.length === 0 ? (
                                    <tr>
                                      <td colSpan={7}>No companies found.</td>
                                    </tr>
                                  ) : (
                                    paginatedCompanies.map(company => (
                                      <tr key={company.id}>
                                        <td
                                          onClick={() => openCompanyModal(company)}
                                          className="zero-company-name clickable-company-name"
                                        >
                                          <div>{company.name}</div>
                                        </td>
                                        <td>{company.industry || '-'}</td>
                                        <td>{company.number_of_employees || '-'}</td>
                                        <td>{company.city && company.country ? `${company.city}, ${company.country}` : company.city || company.country || '-'}</td>
                                        <td>{company.updated_at ? new Date(company.updated_at).toLocaleDateString() : '-'}</td>
                                        <td>
                                          {company.website_url && (
                                            <a
                                              href={company.website_url}
                                              target="_blank"
                                              rel="noopener noreferrer"
                                              className="zero-company-website-link"
                                            >
                                              Website
                                            </a>
                                          )}
                                        </td>
                                      </tr>
                                    ))
                                  )}
                                </tbody>
                            </table>
                        </div>
                        {/* Company Pagination Controls */}
                        <div className="zero-pagination-controls">
                            <div className="zero-pagination-info">
                                Page {companyPage} of {totalCompanyPages} ({totalCompanies} companies)
                            </div>
                            <div className="zero-pagination-buttons">
                                <button
                                    onClick={() => setCompanyPage(prev => Math.max(prev - 1, 1))}
                                    disabled={companyPage === 1 || totalCompanies === 0}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="15 18 9 12 15 6"></polyline>
                                    </svg>
                                </button>
                                <button
                                    onClick={() => setCompanyPage(prev => Math.min(prev + 1, totalCompanyPages))}
                                    disabled={companyPage === totalCompanyPages || totalCompanies === 0}
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="9 18 15 12 9 6"></polyline>
                                    </svg>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                
                <div id="deals" className="zero-section">
                    <div className="zero-section-header">
                        <h2>Deals</h2>
                        <p className="zero-section-description">
                            Track your sales pipeline and opportunities.
                        </p>
                    </div>
                    
                    <div className="zero-table-header">
                        <div className="zero-table-search">
                            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="11" cy="11" r="8"></circle>
                                <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                            </svg>
                            <input type="text" placeholder="Search deals..." />
                        </div>
                        <div className="zero-table-actions">
                            <button className="zero-table-button zero-filter-button">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
                                </svg>
                                Filter
                            </button>
                            <button className="zero-table-button zero-add-button">
                                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <line x1="12" y1="5" x2="12" y2="19"></line>
                                    <line x1="5" y1="12" x2="19" y2="12"></line>
                                </svg>
                                Add Deal
                            </button>
                        </div>
                    </div>
                    
                    <div className="zero-kanban-container">
                        {dealsLoading ? (
                            // Skeleton loader for Kanban board
                            dealStages.map(stage => (
                                <div key={stage} className="zero-kanban-column zero-skeleton-column">
                                    <div className="zero-kanban-column-header">
                                        <div className="zero-skeleton-cell" style={{ width: '100px', height: '20px', marginBottom: '10px' }}></div>
                                        <div className="zero-skeleton-cell" style={{ width: '80px', height: '16px' }}></div>
                                    </div>
                                    <div className="zero-kanban-cards">
                                        {Array.from({ length: 2 }).map((_, idx) => (
                                            <div key={idx} className="zero-kanban-card zero-skeleton-card">
                                                <div className="zero-skeleton-cell" style={{ width: '80%', height: '18px', marginBottom: '8px' }}></div>
                                                <div className="zero-skeleton-cell" style={{ width: '60%', height: '16px', marginBottom: '8px' }}></div>
                                                <div className="zero-skeleton-cell" style={{ width: '40%', height: '14px' }}></div>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))
                        ) : (
                            // Render actual Kanban columns
                            dealStages.map(stage => (
                                <div key={stage} className="zero-kanban-column">
                                    <div className="zero-kanban-column-header">
                                        <h4>
                                          <div className="th-content">
                                            {renderStageIcon(stage)}
                                            <span>{stage}</span>
                                          </div>
                                        </h4>
                                        <div className="zero-column-summary">
                                            <span>{dealsByStage[stage].deals.length} deal{dealsByStage[stage].deals.length !== 1 ? 's' : ''}</span><br/>
                                            <span>{formatCurrency(dealsByStage[stage].totalAmount)}</span>
                                        </div>
                                    </div>
                                    <div className="zero-kanban-cards">
                                        {dealsByStage[stage].deals.length === 0 ? (
                                            <p className="zero-no-deals">No deals in this stage.</p>
                                        ) : (
                                            dealsByStage[stage].deals.map(deal => (
                                                <div 
                                                    key={deal.id} 
                                                    className="zero-kanban-card"
                                                    onClick={() => openDealModal(deal)}
                                                >
                                                    <div className="zero-kanban-card-company"><div>{deal.company_name}</div></div>
                                                    <h4 className="zero-kanban-card-title">{deal.deal_name}</h4>
                                                    <div className="zero-kanban-card-details">
                                                        <span className="zero-kanban-card-amount">{formatCurrency(parseFloat(deal.amount) || 0)}</span>
                                                    </div>
                                                    <div className="zero-kanban-card-date"><span>{formatDate(deal.close_date || deal.last_activity_date)}</span></div>
                                                </div>
                                            ))
                                        )}
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>


                {/* Added ID for section targeting from mobile nav */}
                <div id="assistant" className="zero-section">
                    <div className="zero-section-header">
                        <h2>AI Meeting Notes Assistant</h2>
                        <p className="zero-section-description">
                            Sophie AI Meeting Notes Assistant automatically captures and summarizes your meetings, 
                            then updates your CRM with key information, action items, and follow-ups.
                        </p>
                    </div>
                    
                    <div className="zero-extension-container">
                        <div className="zero-extension-download">
                            <div className="zero-chrome-icon">
                                <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                                    <circle cx="12" cy="12" r="10"></circle>
                                    <circle cx="12" cy="12" r="4"></circle>
                                    <line x1="21.17" y1="8" x2="12" y2="8"></line>
                                    <line x1="3.95" y1="6.06" x2="8.54" y2="14"></line>
                                    <line x1="10.88" y1="21.94" x2="15.46" y2="14"></line>
                                </svg>
                            </div>
                            <h3>Chrome Extension</h3>
                            <p>Download our Chrome extension to enable AI meeting assistance</p>
                            <a href="/chrome_extension/sophieai_extension.zip" download className="zero-download-button">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="download-icon">
                                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                                    <polyline points="7 10 12 15 17 10"></polyline>
                                    <line x1="12" y1="15" x2="12" y2="3"></line>
                                </svg>
                                Download Extension
                            </a>
                        </div>
                        <div className="zero-extension-instructions">
                            <h4>How to Install:</h4>
                            <ol>
                                <li>Download the extension ZIP file.</li>
                                <li>Unzip the downloaded file on your computer.</li>
                                <li>Open Chrome and go to <code>chrome://extensions</code>.</li>
                                <li>Enable "Developer mode" in the top right corner.</li>
                                <li>Click "Load unpacked" and select the unzipped folder.</li>
                                <li>The Sophie AI extension icon will appear in your toolbar.</li>
                            </ol>
                        </div>
                    </div>
                </div>
            </div>

            {/* Mobile Navigation */}
            <div className="zero-mobile-nav">
                <div className="zero-mobile-nav-inner">
                    <a href="#contacts" className="zero-mobile-nav-link">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                            <circle cx="12" cy="7" r="4"></circle>
                        </svg>
                        Contacts
                    </a>
                    <a href="#companies" className="zero-mobile-nav-link">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"></path>
                            <polyline points="9 22 9 12 15 12 15 22"></polyline>
                        </svg>
                        Companies
                    </a>
                    <a href="#deals" className="zero-mobile-nav-link">
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                            <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
                        </svg>
                        Deals
                    </a> 
                    {/* Logout icon button */}
                    <button
                        onClick={handleLogout}
                        className="zero-mobile-nav-link"
                        aria-label="Logout"
                        style={{ border: 'none', background: 'none', padding: 0 }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20"
                             viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
                            <polyline points="16 17 21 12 16 7"/>
                            <line x1="21" y1="12" x2="9" y2="12"/>
                        </svg>
                    </button>
                </div>
            </div>

            {/* Company Details Modal with delete button */}
            {isCompanyModalOpen && selectedCompany && (
                <div className="company-modal">
                    <div className="company-modal-content">
                        <button onClick={closeCompanyModal} className="modal-close-button">
                            ✕
                        </button>
                        <h2>{selectedCompany.name}</h2>
                        
                        {/* Existing modal content */}
                        <div className="modal-info-grid">
                            <div className="modal-info-item">
                                <span className="modal-info-label">Industry</span>
                                <div className="modal-info-value">{selectedCompany.industry || 'Not specified'}</div>
                            </div>
                            
                            <div className="modal-info-item">
                                <span className="modal-info-label">Employees</span>
                                <div className="modal-info-value">{selectedCompany.number_of_employees || 'Not specified'}</div>
                            </div>
                            
                            <div className="modal-info-item">
                                <span className="modal-info-label">Location</span>
                                <div className="modal-info-value">
                                    {selectedCompany.city && selectedCompany.country 
                                        ? `${selectedCompany.city}, ${selectedCompany.country}` 
                                        : selectedCompany.city || selectedCompany.country || 'Not specified'}
                                </div>
                            </div>
                            
                            <div className="modal-info-item">
                                <span className="modal-info-label">Website</span>
                                <div className="modal-info-value">
                                    {selectedCompany.website_url ? (
                                        <a href={selectedCompany.website_url} target="_blank" rel="noopener noreferrer">
                                            {selectedCompany.website_url}
                                        </a>
                                    ) : 'Not available'}
                                </div>
                            </div>
                        </div>
                        
                        {selectedCompany.description && (
                            <div className="modal-description">
                                <span className="modal-info-label">About</span>
                                <div className="modal-info-value">{selectedCompany.description}</div>
                            </div>
                        )}
                        
                        {/* Add Delete Button */}
                        <div className="modal-actions">
                            <button 
                                onClick={handleDeleteCompany}
                                disabled={deletingCompany}
                                className="modal-delete-button"
                            >
                                {deletingCompany ? 'Deleting...' : 'Delete Company'}
                            </button>
                            {deleteError && <div className="modal-error">{deleteError}</div>}
                        </div>
                    </div>
                </div>
            )}

            {/* Contact Details Modal with delete button */}
            {isContactModalOpen && selectedContact && (
                <div className="company-modal contact-modal">
                    <div className="company-modal-content">
                        <button onClick={closeContactModal} className="modal-close-button">
                            ✕
                        </button>
                        <h2>{selectedContact.first_name} {selectedContact.last_name}</h2>
                        
                        {/* Existing modal content */}
                        <div className="modal-info-grid">
                            <div className="modal-info-item">
                                <span className="modal-info-label">Company</span>
                                <div className="modal-info-value">{selectedContact.company_name || 'Not specified'}</div>
                            </div>
                            
                            <div className="modal-info-item">
                                <span className="modal-info-label">Job Title</span>
                                <div className="modal-info-value">{selectedContact.job_title || 'Not specified'}</div>
                            </div>
                            
                            <div className="modal-info-item">
                                <span className="modal-info-label">Email</span>
                                <div className="modal-info-value">
                                    <a href={`mailto:${selectedContact.email}`}>{selectedContact.email}</a>
                                </div>
                            </div>
                            
                            <div className="modal-info-item">
                                <span className="modal-info-label">Phone</span>
                                <div className="modal-info-value">
                                    {selectedContact.phone_number || selectedContact.mobile_phone_number || 'Not specified'}
                                </div>
                            </div>
                        </div>
                        
                        {(selectedContact.street_address || selectedContact.city || selectedContact.state_region || 
                          selectedContact.postal_code || selectedContact.country_region) && (
                            <div className="modal-info-item" style={{marginTop: '15px'}}>
                                <span className="modal-info-label">Address</span>
                                <div className="modal-info-value">
                                    {`${selectedContact.street_address || ''} ${selectedContact.city || ''} ${selectedContact.state_region || ''} ${selectedContact.postal_code || ''} ${selectedContact.country_region || ''}`.trim()}
                                </div>
                            </div>
                        )}
                        
                        {selectedContact.description && (
                            <div className="modal-description">
                                <span className="modal-info-label">Notes</span>
                                <div className="modal-info-value">{selectedContact.description}</div>
                            </div>
                        )}
                        
                        {/* Add Delete Button */}
                        <div className="modal-actions">
                            <button 
                                onClick={handleDeleteContact}
                                disabled={deletingContact}
                                className="modal-delete-button"
                            >
                                {deletingContact ? 'Deleting...' : 'Delete Contact'}
                            </button>
                            {deleteError && <div className="modal-error">{deleteError}</div>}
                        </div>
                    </div>
                </div>
            )}

            {/* Deal Details Modal with delete button */}
            {isDealModalOpen && selectedDeal && (
                <div className="company-modal deal-modal">
                    <div className="company-modal-content">
                        <button onClick={closeDealModal} className="modal-close-button">
                            ✕
                        </button>
                        <h2>
                            {selectedDeal.deal_name}
                            <span className={`modal-status-badge ${selectedDeal.deal_stage === 'Closed Won' ? 'won' : selectedDeal.deal_stage === 'Closed Lost' ? 'cold' : 'active'}`}>
                                {selectedDeal.deal_stage}
                            </span>
                        </h2>
                        
                        {/* Existing modal content */}
                        <div className="modal-info-grid">
                            <div className="modal-info-item">
                                <span className="modal-info-label">Company</span>
                                <div className="modal-info-value">{selectedDeal.company_name}</div>
                            </div>
                            
                            <div className="modal-info-item">
                                <span className="modal-info-label">Amount</span>
                                <div className="modal-info-value">{selectedDeal.amount ? formatCurrency(parseFloat(selectedDeal.amount)) : 'Not specified'}</div>
                            </div>
                            
                            {selectedDeal.close_date && (
                                <div className="modal-info-item">
                                    <span className="modal-info-label">Close Date</span>
                                    <div className="modal-info-value">{formatDate(selectedDeal.close_date)}</div>
                                </div>
                            )}
                            
                            {selectedDeal.last_activity_date && (
                                <div className="modal-info-item">
                                    <span className="modal-info-label">Last Activity</span>
                                    <div className="modal-info-value">{formatDate(selectedDeal.last_activity_date)}</div>
                                </div>
                            )}
                            
                            {selectedDeal.next_step && (
                                <div className="modal-info-item">
                                    <span className="modal-info-label">Next Step</span>
                                    <div className="modal-info-value">{selectedDeal.next_step}</div>
                                </div>
                            )}
                            
                            {selectedDeal.owner_email && (
                                <div className="modal-info-item">
                                    <span className="modal-info-label">Owner</span>
                                    <div className="modal-info-value">
                                        <a href={`mailto:${selectedDeal.owner_email}`}>{selectedDeal.owner_email}</a>
                                    </div>
                                </div>
                            )}
                        </div>
                        
                        {selectedDeal.deal_description && (
                            <div className="modal-description">
                                <span className="modal-info-label">Description</span>
                                <div className="markdown-content">
                                    <ReactMarkdown>{selectedDeal.deal_description}</ReactMarkdown>
                                </div>
                            </div>
                        )}
                        
                        {/* Add Delete Button */}
                        <div className="modal-actions">
                            <button 
                                onClick={handleDeleteDeal}
                                disabled={deletingDeal}
                                className="modal-delete-button"
                            >
                                {deletingDeal ? 'Deleting...' : 'Delete Deal'}
                            </button>
                            {deleteError && <div className="modal-error">{deleteError}</div>}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default DemoCRM;